import type { Edge } from 'reactflow';
import type { CustomNode } from '../types/nodes';
import type { ContextField, NodeContext } from '../types/workflow-context';
import { getAvailableFields } from './workflow-utils';

/**
 * Standardized interface for displaying variables across all nodes
 */
export interface DisplayVariable {
  id: string;           // Full field ID (e.g., "form.fieldName" or "api-call-2.createdAt")
  cleanName: string;    // Clean name for display (e.g., "fieldName" or "form_fieldName" if duplicate)
  source: string;       // Source identifier (e.g., "form", "api-call-2", "execution")
  context: NodeContext; // Full context information
  field: ContextField;  // The actual field data
}

/**
 * Get standardized, unique field names for display across all nodes
 * This function ensures consistent variable naming and prevents conflicts
 */
export function getStandardizedVariables(
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[]
): DisplayVariable[] {
  const contexts = getAvailableFields(nodeId, nodes, edges);
  
  // Track used field names to prevent duplicates
  const usedFieldNames = new Set<string>();
  const variables: DisplayVariable[] = [];

  // Process contexts from upstream nodes
  for (const context of contexts) {
    for (const field of context.fields) {
      // For start nodes (start.webhook, start.form, start.schedule),
      // don't prepend the node ID since their data becomes the root context
      const isStartNode = context.nodeType.startsWith('start.');
      const fullFieldId = isStartNode ? field.id : `${context.nodeId}.${field.id}`;
      const [source] = fullFieldId.split('.');

      // Get the clean field name (without source prefix)
      let cleanFieldName = field.id.includes('.') ? field.id.split('.').pop() || field.id : field.id;

      // If the clean field name is already used, make it unique
      if (usedFieldNames.has(cleanFieldName)) {
        // Add source prefix to make it unique
        const sourcePrefix = source === 'form' ? 'form' : source === 'execution' ? 'exec' : source;
        cleanFieldName = `${sourcePrefix}_${cleanFieldName}`;
      }

      // Mark this field name as used
      usedFieldNames.add(cleanFieldName);

      variables.push({
        id: fullFieldId,
        cleanName: cleanFieldName,
        source,
        context,
        field
      });
    }
  }

  return variables;
}

/**
 * Get variables grouped by source for organized display
 */
export function getGroupedVariables(
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[]
): Record<string, DisplayVariable[]> {
  const variables = getStandardizedVariables(nodeId, nodes, edges);
  const grouped: Record<string, DisplayVariable[]> = {};
  
  variables.forEach(variable => {
    if (!grouped[variable.source]) {
      grouped[variable.source] = [];
    }
    grouped[variable.source].push(variable);
  });
  
  return grouped;
}

/**
 * Generate the display text for a variable (e.g., "{{fieldName}}")
 */
export function getVariableDisplayText(variable: DisplayVariable): string {
  return `{{${variable.cleanName}}}`;
}

/**
 * Generate the full variable reference for internal use (e.g., "{{form.fieldName}}")
 */
export function getVariableReference(variable: DisplayVariable): string {
  return `{{${variable.id}}}`;
}
